import { AgentTemplateResponse } from 'apis/agency/agentTemplate/types';
import { Box, IconButton, Typography, Avatar, Chip, Stack, useTheme, Grid2, CircularProgress } from '@mui/material';
import { MdClose } from 'react-icons/md';
import { titleCase } from 'utils/helperFunctions';
import { FiCheckCircle } from 'react-icons/fi';
import { useGetAgencyAgentTemplateMoreData } from '@/apis/agency/integrations/hook';

interface AgentDetailsDrawerProps {
  template: AgentTemplateResponse | null;
  onClose: () => void;
}

const AgentDetailsDrawer = ({ template, onClose }: AgentDetailsDrawerProps) => {
  const theme = useTheme();
  const { data: workflowsData, isLoading } = useGetAgencyAgentTemplateMoreData(template?.id);
  if (!template) return null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' fontWeight='bold'>
            Agent Details
          </Typography>
          <IconButton onClick={onClose}>
            <MdClose />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
        {/* Agent Profile */}
        <Box mb={3}>
          <Box display='flex' alignItems='center' gap={2} mb={2}>
            <Avatar
              src={template.avatar.url || undefined}
              sx={{ width: 90, height: 90, border: `4px solid ${theme.palette.primary.light}` }}
            >
              {template.agent_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant='h5' fontWeight='bold' mb={1}>
                {template.agent_name?.replace(/_/g, ' ')}
              </Typography>
              <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                <Chip
                  label={titleCase(template.agent_type)}
                  size='small'
                  sx={{ bgcolor: theme.palette.primary.light, color: '#000', borderRadius: '16px', paddingX: '5px' }}
                  variant='filled'
                />
                <Chip
                  label={titleCase(template.call_direction)}
                  size='small'
                  sx={{ bgcolor: theme.palette.primary.light, color: '#000', borderRadius: '16px', paddingX: '5px' }}
                  variant='filled'
                />
                <Chip
                  label={`${template?.tags?.[0]}`}
                  size='small'
                  sx={{ bgcolor: theme.palette.primary.light, color: '#000', borderRadius: '16px', paddingX: '5px' }}
                  variant='filled'
                />
              </Stack>
            </Box>
          </Box>
        </Box>
        {/* About This Agent */}
        <Box
          mb={3}
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 2,
            p: 2
          }}
        >
          <Typography variant='h6' fontWeight='bold' mb={1}>
            About This Agent
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {template.about}
          </Typography>
        </Box>

        {/* iTrust Integration */}
        <Box
          mb={3}
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 2,
            p: 2
          }}
        >
          <Box display='flex' alignItems='center' gap={2} mb={2}>
            <Box>
              <Typography variant='h6' fontWeight='bold'>
                Key Workflows
              </Typography>
            </Box>
          </Box>
          <Grid2 container spacing={2}>
            {isLoading ? (
              <Box display='flex' justifyContent='center' width='100%' my={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              workflowsData?.data?.map((item: any) => (
                <Grid2 size={{ xs: 12, sm: 6 }} key={item}>
                  <Box display='flex' alignItems='center' gap={1}>
                    <FiCheckCircle color='#22C55E' size={20} />
                    <Typography variant='body1' color='text.primary'>
                      {titleCase(item.replace(/_/g, ' '))}
                    </Typography>
                  </Box>
                </Grid2>
              ))
            )}
          </Grid2>
        </Box>
      </Box>
    </Box>
  );
};

export default AgentDetailsDrawer;
